/**
 * CodeMirror 6 extension that enforces a read-only region at the top of the
 * document. Everything before the boundary position is frozen; user edits
 * are only allowed after it. Programmatic transactions (tagged with
 * `programmaticAnnotation`) bypass the filter so the React sync layer can
 * freely update the conversation history.
 * @module chatReadOnlyExtension
 * @category Editor
 */

import {
	Annotation,
	EditorSelection,
	EditorState,
	type Range,
	RangeSet,
	StateEffect,
	StateField,
} from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, WidgetType, type ViewUpdate } from "@codemirror/view";

// ── Chat Button Events ──────────────────────────────────────────────

/** Custom event fired when user clicks the Create button in chat */
export const CREATE_MODE_EVENT = "qp:chat:create-mode";
/** Custom event fired when user clicks the Cancel button during streaming */
export const CANCEL_STREAM_EVENT = "qp:chat:cancel-stream";
/** Custom event fired when user clicks an AI tour link ([…](qp://tour/<section>)) */
export const TOUR_JUMP_EVENT = "qp:chat:tour-jump";

export function fireCreateModeEvent() {
	window.dispatchEvent(new CustomEvent(CREATE_MODE_EVENT));
}

export function fireCancelStreamEvent() {
	window.dispatchEvent(new CustomEvent(CANCEL_STREAM_EVENT));
}

export function fireTourJumpEvent(section: string) {
	window.dispatchEvent(
		new CustomEvent<{ section: string }>(TOUR_JUMP_EVENT, {
			detail: { section },
		}),
	);
}

// ── Cancel Button Widget ────────────────────────────────────────────

class CancelButtonWidget extends WidgetType {
	toDOM(): HTMLElement {
		const button = document.createElement("button");
		button.textContent = "Cancel";
		button.className = "cm-cancel-button";
		button.type = "button";
		button.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			fireCancelStreamEvent();
		});
		return button;
	}

	eq(): boolean {
		return true;
	}

	ignoreEvent(): boolean {
		return false;
	}
}

// ── Create Prompt Widget ────────────────────────────────────────────

class CreatePromptWidget extends WidgetType {
	toDOM(): HTMLElement {
		const wrapper = document.createElement("div");
		wrapper.className = "cm-create-prompt";
		wrapper.setAttribute("role", "group");
		wrapper.setAttribute("aria-label", "Create presentation prompt");

		const text = document.createElement("span");
		text.textContent = "Ready to create or keep planning? ";
		text.className = "cm-create-prompt-text";

		const button = document.createElement("button");
		button.textContent = "Create";
		button.className = "cm-create-prompt-button";
		button.type = "button";
		button.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			fireCreateModeEvent();
		});

		wrapper.appendChild(text);
		wrapper.appendChild(button);
		return wrapper;
	}

	eq(): boolean {
		return true; // All instances are equivalent
	}

	ignoreEvent(): boolean {
		return false; // Allow events to pass through
	}
}

// ── Tour Jump Widget ────────────────────────────────────────────────

/** Replaces an AI `[label](qp://tour/<section>)` link with a button that
 *  opens the tour at that section (handled in ChatCodeMirror). */
class TourJumpWidget extends WidgetType {
	constructor(
		private label: string,
		private section: string,
	) {
		super();
	}

	toDOM(): HTMLElement {
		const button = document.createElement("button");
		button.textContent = this.label;
		button.className = "cm-tour-jump-button";
		button.type = "button";
		const section = this.section;
		button.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			fireTourJumpEvent(section);
		});
		return button;
	}

	eq(other: TourJumpWidget): boolean {
		return other.label === this.label && other.section === this.section;
	}

	ignoreEvent(): boolean {
		return false;
	}
}

/** Dispatch this effect to move the read-only boundary to a new position. */
export const readOnlyBoundaryEffect = StateEffect.define<number>();

/** Tag a transaction with this annotation to bypass the read-only filter. */
export const programmaticAnnotation = Annotation.define<boolean>();

/** Tracks the character position where the read-only region ends. */
export const readOnlyBoundaryField = StateField.define<number>({
	create: () => 0,
	update(boundary, tr) {
		// Shift the boundary when the document changes (e.g. programmatic inserts).
		// assoc -1: boundary stays put when text is inserted at its position
		// (user typing), but still shifts right for insertions before it.
		boundary = tr.changes.mapPos(boundary, -1);
		for (const e of tr.effects) {
			if (e.is(readOnlyBoundaryEffect)) return e.value;
		}
		return boundary;
	},
});

/**
 * Change filter that rejects any transaction whose changes touch text
 * before the read-only boundary — unless the transaction is tagged as
 * programmatic.
 */
export const readOnlyFilter = EditorState.changeFilter.of((tr) => {
	if (tr.annotation(programmaticAnnotation)) return true;
	const boundary = tr.startState.field(readOnlyBoundaryField);
	if (boundary === 0) return true;
	let block = false;
	tr.changes.iterChanges((fromA) => {
		if (fromA < boundary) block = true;
	});
	return !block;
});

/**
 * Transaction filter that clamps cursor/selection so it cannot enter
 * the read-only region. Programmatic transactions are exempt.
 */
export const cursorBoundaryFilter = EditorState.transactionFilter.of((tr) => {
	if (tr.annotation(programmaticAnnotation)) return tr;
	const boundary = tr.state.field(readOnlyBoundaryField);
	if (boundary === 0) return tr;

	let needsClamp = false;
	for (const range of tr.newSelection.ranges) {
		if (range.from < boundary || range.to < boundary) {
			needsClamp = true;
			break;
		}
	}
	if (!needsClamp) return tr;

	return [
		tr,
		{
			selection: EditorSelection.create(
				tr.newSelection.ranges.map((r) =>
					EditorSelection.range(
						Math.max(r.anchor, boundary),
						Math.max(r.head, boundary),
					),
				),
				tr.newSelection.mainIndex,
			),
		},
	];
});

// ── Chat role line decorations ──────────────────────────────────────

const userLineDeco = Decoration.line({ class: "cm-chat-user" });
const aiLineDeco = Decoration.line({ class: "cm-chat-ai" });
const userDotDeco = Decoration.mark({ class: "cm-chat-dot-user" });
const aiDotDeco = Decoration.mark({ class: "cm-chat-dot-ai" });
const createPromptWidget = Decoration.widget({
	widget: new CreatePromptWidget(),
	side: 1, // After the content
});
const cancelButtonWidget = Decoration.widget({
	widget: new CancelButtonWidget(),
	side: 1, // After the content
});

function buildRoleDecorations(view: EditorView): RangeSet<Decoration> {
	const decos: Range<Decoration>[] = [];
	let section: "user" | "ai" | null = null;

	for (let i = 1; i <= view.state.doc.lines; i++) {
		const line = view.state.doc.line(i);
		if (line.text.startsWith("● You:")) {
			// Insert create prompt widget at end of previous AI section
			if (section === "ai") {
				// Widget appears on the line before "● You:" - get previous line end
				const prevLine = view.state.doc.line(i - 1);
				decos.push(createPromptWidget.range(prevLine.to));
			}
			section = "user";
			decos.push(userLineDeco.range(line.from));
			decos.push(userDotDeco.range(line.from, line.from + 1)); // color the dot
		} else if (line.text.startsWith("● AI:")) {
			section = "ai";
			decos.push(aiLineDeco.range(line.from));
			decos.push(aiDotDeco.range(line.from, line.from + 1)); // color the dot
		} else if (line.text.startsWith("● Greg:")) {
			// human support reply — reads like an AI section, user-coloured dot
			section = "ai";
			decos.push(aiLineDeco.range(line.from));
			decos.push(userDotDeco.range(line.from, line.from + 1));
		} else if (line.text === "Thinking...") {
			// Add cancel button inline with "Thinking..."
			decos.push(aiLineDeco.range(line.from));
			decos.push(cancelButtonWidget.range(line.to));
		} else if (section === "ai" && line.text.length > 0) {
			decos.push(aiLineDeco.range(line.from));
		}
	}

	// Widget is only added when we see "● You:" after an AI section (inside the loop)
	// This ensures we don't show the prompt while AI is still streaming

	return RangeSet.of(decos, true);
}

/** ViewPlugin that applies `.cm-chat-user` / `.cm-chat-ai` classes to role header lines. */
export const chatRoleDecorations = ViewPlugin.fromClass(
	class {
		decorations: RangeSet<Decoration>;
		constructor(view: EditorView) {
			this.decorations = buildRoleDecorations(view);
		}
		update(update: ViewUpdate) {
			if (update.docChanged) {
				this.decorations = buildRoleDecorations(update.view);
			}
		}
	},
	{ decorations: (v) => v.decorations },
);

// ── Markdown rendering decorations ──────────────────────────────────

class BulletWidget extends WidgetType {
	toDOM(): HTMLElement {
		const span = document.createElement("span");
		span.textContent = "  · ";
		span.className = "cm-md-bullet";
		return span;
	}
	eq(): boolean { return true; }
}

const hiddenMark = Decoration.replace({});
const bulletReplace = Decoration.replace({ widget: new BulletWidget() });
const boldMark = Decoration.mark({ class: "cm-md-bold" });
const italicMark = Decoration.mark({ class: "cm-md-italic" });
const codeMark = Decoration.mark({ class: "cm-md-code" });
const headingMark = Decoration.mark({ class: "cm-md-heading" });

function buildMarkdownDecorations(view: EditorView): RangeSet<Decoration> {
	const decos: Range<Decoration>[] = [];
	const boundary = view.state.field(readOnlyBoundaryField);

	// Per-line decorations (headings, lists)
	for (let i = 1; i <= view.state.doc.lines; i++) {
		const line = view.state.doc.line(i);
		if (line.from >= boundary) break;

		// Headings: ### text — hide the hashes, bold the text
		const headingMatch = line.text.match(/^(#{1,3})\s+/);
		if (headingMatch) {
			const prefixLen = headingMatch[0].length;
			decos.push(hiddenMark.range(line.from, line.from + prefixLen));
			decos.push(headingMark.range(line.from + prefixLen, line.to));
			continue;
		}

		// Bullet list: - text or * text — replace marker with ·
		const bulletMatch = line.text.match(/^(\s*[-*])\s/);
		if (bulletMatch) {
			decos.push(bulletReplace.range(line.from, line.from + bulletMatch[1].length + 1));
			continue;
		}

		// Numbered list: 1. text — replace "1." with "1."  (just hide the dot+space noise)
		const numMatch = line.text.match(/^(\s*\d+)\.\s/);
		if (numMatch) {
			const numEnd = line.from + numMatch[1].length;
			decos.push(hiddenMark.range(numEnd, numEnd + 1)); // hide the dot
			continue;
		}
	}

	// Inline decorations within the read-only region
	const text = view.state.doc.sliceString(0, boundary);

	// Bold: **text**
	for (const m of text.matchAll(/\*\*(.+?)\*\*/g)) {
		if (m.index == null) continue;
		const from = m.index;
		const to = from + m[0].length;
		decos.push(hiddenMark.range(from, from + 2));
		decos.push(boldMark.range(from + 2, to - 2));
		decos.push(hiddenMark.range(to - 2, to));
	}

	// Italic: *text* (but not inside bold **)
	for (const m of text.matchAll(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g)) {
		if (m.index == null) continue;
		const from = m.index;
		const to = from + m[0].length;
		decos.push(hiddenMark.range(from, from + 1));
		decos.push(italicMark.range(from + 1, to - 1));
		decos.push(hiddenMark.range(to - 1, to));
	}

	// Inline code: `text`
	for (const m of text.matchAll(/`([^`]+)`/g)) {
		if (m.index == null) continue;
		const from = m.index;
		const to = from + m[0].length;
		decos.push(hiddenMark.range(from, from + 1));
		decos.push(codeMark.range(from + 1, to - 1));
		decos.push(hiddenMark.range(to - 1, to));
	}

	// Tour links: [label](qp://tour/<section>) → jump button
	for (const m of text.matchAll(/\[([^\]]+)\]\(qp:\/\/tour\/([a-z]+)\)/g)) {
		if (m.index == null) continue;
		decos.push(
			Decoration.replace({
				widget: new TourJumpWidget(m[1], m[2]),
			}).range(m.index, m.index + m[0].length),
		);
	}

	decos.sort((a, b) => a.from - b.from || a.value.startSide - b.value.startSide);
	return RangeSet.of(decos, true);
}

/** ViewPlugin that renders markdown in the read-only region. */
export const chatMarkdownDecorations = ViewPlugin.fromClass(
	class {
		decorations: RangeSet<Decoration>;
		constructor(view: EditorView) {
			this.decorations = buildMarkdownDecorations(view);
		}
		update(update: ViewUpdate) {
			if (update.docChanged) {
				this.decorations = buildMarkdownDecorations(update.view);
			}
		}
	},
	{ decorations: (v) => v.decorations },
);

/** Base theme for chat role lines and markdown. */
export const chatRoleTheme = EditorView.baseTheme({
	".cm-chat-user": {
		fontWeight: "700",
		color: "#1a1a1a",
	},
	".cm-chat-ai": {
		color: "#4a6fa5",
	},
	".cm-chat-dot-user": {
		color: "#007ACC", // colors.alabaster.active
		fontSize: "0.8em",
		verticalAlign: "middle",
	},
	".cm-chat-dot-ai": {
		color: "#6B4FDB", // purple (brand color)
		fontSize: "0.8em",
		verticalAlign: "middle",
	},
	".cm-md-bold": {
		fontWeight: "700",
	},
	".cm-md-italic": {
		fontStyle: "italic",
	},
	".cm-md-code": {
		fontFamily: "monospace",
		backgroundColor: "rgba(0, 0, 0, 0.06)",
		padding: "1px 4px",
		borderRadius: "3px",
		fontSize: "0.9em",
	},
	".cm-md-heading": {
		fontWeight: "700",
		fontSize: "1.05em",
	},
	".cm-md-bullet": {
		color: "#6B4FDB",
	},
	".cm-create-prompt": {
		display: "block",
		padding: "4px 0",
		color: "#4a6fa5", // Same as AI text
		fontFamily: "Avantt-Regular, sans-serif",
	},
	".cm-create-prompt-text": {
		color: "#4a6fa5", // Same as AI text
	},
	".cm-create-prompt-button": {
		padding: "2px 10px",
		marginLeft: "12px",
		border: "1.5px solid #007ACC",
		borderRadius: "4px",
		background: "transparent",
		color: "#007ACC",
		fontFamily: "Avantt-Regular, sans-serif",
		fontSize: "0.8rem",
		fontWeight: "600",
		cursor: "pointer",
		transition: "background 0.15s, color 0.15s",
	},
	".cm-tour-jump-button": {
		padding: "2px 10px",
		border: "1.5px solid #007ACC",
		borderRadius: "4px",
		background: "transparent",
		color: "#007ACC",
		fontFamily: "Avantt-Regular, sans-serif",
		fontSize: "0.8rem",
		fontWeight: "600",
		cursor: "pointer",
		transition: "background 0.15s, color 0.15s",
	},
	".cm-tour-jump-button:hover": {
		background: "#007ACC",
		color: "#fff",
	},
	".cm-tour-jump-button:focus-visible": {
		outline: "2px solid #007ACC",
		outlineOffset: "2px",
	},
	".cm-cancel-button": {
		padding: "2px 10px",
		marginLeft: "12px",
		border: "1.5px solid #007ACC",
		borderRadius: "4px",
		background: "transparent",
		color: "#007ACC",
		fontFamily: "Avantt-Regular, sans-serif",
		fontSize: "0.8rem",
		fontWeight: "600",
		cursor: "pointer",
		transition: "background 0.15s, color 0.15s",
	},
	".cm-cancel-button:hover": {
		background: "#007ACC",
		color: "#fff",
	},
	".cm-cancel-button:focus-visible": {
		outline: "2px solid #007ACC",
		outlineOffset: "2px",
	},
	".cm-create-prompt-button:hover": {
		background: "#007ACC",
		color: "#fff",
	},
	".cm-create-prompt-button:focus-visible": {
		outline: "2px solid #007ACC",
		outlineOffset: "2px",
	},
});
